import { useId, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Botao, Campo, Entrada, Falha, Selecao, Selo, cx } from "../../components/ui.js";
import { ROTULO_DO_MODO, type ModoDoAtendimento } from "./etapas.js";

export interface Identificacao {
  nome: string;
  vivenciaId: string | null;
  /** O Local, só no "Só fotografia" (#101): sem vivência, é por ele que a foto chega ao Local. */
  institutionId: string | null;
  participantId: string | null;
}

/** Um Local que o "Só fotografia" pode escolher: os ativos do dono. */
export interface LocalDaFoto {
  id: string;
  name: string;
}

/**
 * Quem está sendo atendido.
 *
 * O caminho rápido é digitar o WhatsApp: quem já participou antes aparece na
 * hora, com o nome preenchido. Cadastrar do zero é o caminho longo, e por isso
 * fica atrás de um clique.
 */
export function EtapaQuem({
  valor,
  aoMudar,
  modo,
  aoMudarModo,
  locais,
}: {
  valor: Identificacao;
  aoMudar: (v: Identificacao) => void;
  modo: ModoDoAtendimento;
  /** Os Locais ativos; `undefined` enquanto a lista não chega. */
  locais: readonly LocalDaFoto[] | undefined;
  /**
   * Sem ela, o modo não muda mais: o atendimento com IA já criou a geração (ao sair da foto), e trocar para
   * "Só fotografia" deixaria um card sem imagem para trás. Para fotografar sem IA, começa-se outro.
   */
  aoMudarModo?: ((m: ModoDoAtendimento) => void) | undefined;
}) {
  const [busca, setBusca] = useState("");
  const [cadastrando, setCadastrando] = useState(false);
  const [sobrenome, setSobrenome] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const vivencias = trpc.vivencias.listActive.useQuery();

  const encontrado = trpc.participants.findByWhatsapp.useQuery(
    { whatsapp: busca },
    { enabled: busca.replace(/\D/g, "").length >= 10 },
  );

  const criar = trpc.participants.create.useMutation({
    onSuccess: (p) => {
      aoMudar({ ...valor, nome: `${p.firstName} ${p.lastName}`, participantId: p.id });
      setCadastrando(false);
      setErro(null);
    },
    onError: (e) => setErro(e.message),
  });

  const achado = encontrado.data;

  return (
    <div className="flex flex-col gap-5">
      {aoMudarModo && (
        <EscolhaDoModo
          modo={modo}
          aoMudar={(m) => {
            aoMudarModo(m);
            // Só fotografia não tem vivência (decisão do usuário): a escolhida antes sai, e não volta escondida.
            if (m === "fotografia" && valor.vivenciaId) aoMudar({ ...valor, vivenciaId: null });
          }}
        />
      )}

      {modo === "ia" ? (
        <Campo rotulo="Vivência">
          <Selecao
            value={valor.vivenciaId ?? ""}
            onChange={(e) => aoMudar({ ...valor, vivenciaId: e.target.value || null })}
          >
            <option value="">Sem vivência</option>
            {vivencias.data?.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Selecao>
        </Campo>
      ) : (
        <Campo
          rotulo="Local"
          dica="Sem vivência: a foto fica guardada neste Local, e o WhatsApp vai só para a pessoa cadastrada."
          erro={
            locais && locais.length === 0
              ? "Nenhum Local ativo. Cadastre um em Locais/Eventos para salvar só a fotografia."
              : null
          }
        >
          <Selecao
            value={valor.institutionId ?? ""}
            disabled={!locais || locais.length === 0}
            onChange={(e) => aoMudar({ ...valor, institutionId: e.target.value || null })}
          >
            <option value="">{locais ? "Selecione o local" : "Carregando os locais…"}</option>
            {locais?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Selecao>
        </Campo>
      )}

      <Campo rotulo="WhatsApp" dica="Se já participou antes, o nome vem preenchido.">
        <Entrada
          type="tel"
          inputMode="numeric"
          placeholder="(11) 99999-8888"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Campo>

      {achado && (
        <button
          onClick={() =>
            aoMudar({
              ...valor,
              nome: `${achado.firstName} ${achado.lastName}`,
              participantId: achado.id,
            })
          }
          className={cx(
            "flex items-center justify-between rounded-peca border px-4 py-3 text-left transition-colors",
            valor.participantId === achado.id
              ? "border-brasa bg-brasa-fraca"
              : "border-borda bg-superficie hover:border-borda-forte",
          )}
        >
          <span>
            <span className="block text-[0.9375rem] font-medium">
              {achado.firstName} {achado.lastName}
            </span>
            <span className="block font-mono text-[0.75rem] text-texto-suave">
              {achado.whatsappE164}
            </span>
          </span>
          {valor.participantId === achado.id ? (
            <Selo tom="brasa">Selecionado</Selo>
          ) : (
            <Selo tom="quieto">Usar</Selo>
          )}
        </button>
      )}

      {busca.replace(/\D/g, "").length >= 10 &&
        !achado &&
        !encontrado.isLoading &&
        !cadastrando && (
          <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
            <p className="text-[0.9375rem] text-texto-suave">Ninguém com esse número ainda.</p>
            <Botao
              tom="fantasma"
              formato="nu"
              className="mt-1"
              onClick={() => setCadastrando(true)}
            >
              Cadastrar agora
            </Botao>
          </div>
        )}

      {cadastrando && (
        <div className="flex flex-col gap-3 rounded-peca border border-borda bg-superficie p-4">
          <Campo rotulo="Nome">
            <Entrada
              value={valor.nome}
              onChange={(e) => aoMudar({ ...valor, nome: e.target.value })}
              autoFocus
            />
          </Campo>
          <Campo rotulo="Sobrenome">
            <Entrada value={sobrenome} onChange={(e) => setSobrenome(e.target.value)} />
          </Campo>
          {erro && <Falha mensagem={erro} />}
          <div className="flex gap-3">
            <Botao
              tom="brasa"
              carregando={criar.isPending}
              onClick={() =>
                criar.mutate({
                  firstName: valor.nome.trim(),
                  lastName: sobrenome.trim(),
                  whatsapp: busca,
                })
              }
            >
              Cadastrar
            </Botao>
            <Botao tom="fantasma" onClick={() => setCadastrando(false)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}

      {!cadastrando && (
        <Campo
          rotulo="Nome do espectador"
          dica="É o nome que aparece na galeria e no arquivo entregue."
        >
          <Entrada
            value={valor.nome}
            onChange={(e) => aoMudar({ ...valor, nome: e.target.value })}
            placeholder="Maria Aparecida"
          />
        </Campo>
      )}
    </div>
  );
}

const MODOS: { valor: ModoDoAtendimento; rotulo: string; sobre: string }[] = [
  { valor: "ia", rotulo: ROTULO_DO_MODO.ia, sobre: "A IA cria a imagem a partir da foto." },
  {
    valor: "fotografia",
    rotulo: ROTULO_DO_MODO.fotografia,
    sobre: "A foto sai como foi tirada, pronta para imprimir. Sem IA, não usa o pacote.",
  },
];

/**
 * A primeira decisão do atendimento: com IA ou só a fotografia (#95). Rádios de verdade, escondidos atrás dos
 * cartões: o teclado e o leitor de tela enxergam um grupo de duas opções, e as setas trocam de modo.
 */
function EscolhaDoModo({
  modo,
  aoMudar,
}: {
  modo: ModoDoAtendimento;
  aoMudar: (m: ModoDoAtendimento) => void;
}) {
  const nomeDoGrupo = useId();
  const idDaFrase = useId();

  return (
    <fieldset className="flex min-w-0 flex-col gap-2" aria-describedby={idDaFrase}>
      <legend className="mb-2 text-[0.8125rem] font-medium text-texto-suave">
        Como vai ser este atendimento?
      </legend>
      <div className="grid grid-cols-2 gap-2">
        {MODOS.map((m) => (
          <label key={m.valor} className="relative block">
            <input
              type="radio"
              name={nomeDoGrupo}
              value={m.valor}
              checked={modo === m.valor}
              onChange={() => aoMudar(m.valor)}
              className="peer sr-only"
            />
            <span
              className={cx(
                "flex min-h-11 items-center justify-center rounded-peca border px-2 py-2",
                "text-center text-[0.9375rem] leading-tight transition-colors",
                "border-borda text-texto-suave hover:border-borda-forte hover:text-texto",
                "peer-checked:border-brasa peer-checked:bg-brasa-fraca peer-checked:text-brasa",
                "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brasa",
              )}
            >
              {m.rotulo}
            </span>
          </label>
        ))}
      </div>
      <p
        id={idDaFrase}
        aria-live="polite"
        className="text-[0.8125rem] leading-relaxed text-texto-suave"
      >
        {MODOS.find((m) => m.valor === modo)?.sobre}
      </p>
    </fieldset>
  );
}
